import { useEffect, useState } from "react";
import { Download, Share, X, Smartphone } from "lucide-react";

const DISMISS_KEY = "skinsights_pwa_dismissed_at";
const DISMISS_DAYS = 14;

function isIos() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}
function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}
function recentlyDismissed() {
  const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
  if (!ts) return false;
  const days = (Date.now() - ts) / (1000 * 60 * 60 * 24);
  return days < DISMISS_DAYS;
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    // Android / Chromium path
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS path — no event, just show a hint after a small delay if mobile Safari
    let iosTimer;
    if (isIos() && !isStandalone()) {
      iosTimer = setTimeout(() => setIosHint(true), 6000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
    setIosHint(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice?.outcome === "accepted") {
      setShow(false);
    } else {
      dismiss();
    }
  };

  if (show && deferred) {
    return (
      <div
        data-testid="pwa-install-banner"
        className="fixed bottom-24 md:bottom-6 left-1/2 -translate-x-1/2 z-40 w-[92%] max-w-sm bg-white rounded-2xl shadow-2xl border border-stone-200 p-4 flex items-start gap-3 animate-fade-up"
      >
        <div className="w-10 h-10 rounded-xl bg-sage-100 flex items-center justify-center shrink-0">
          <Smartphone className="w-5 h-5 text-sage-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-serif text-lg text-stone-900 leading-tight">Install Skinsights</p>
          <p className="text-xs text-stone-500 mt-0.5">Add to your home screen for one-tap daily logging.</p>
          <div className="mt-3 flex gap-2">
            <button
              onClick={install}
              data-testid="pwa-install-confirm"
              className="inline-flex items-center gap-1.5 rounded-full bg-sage-400 hover:bg-sage-500 text-white text-sm px-4 py-1.5 font-medium transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> Install
            </button>
            <button
              onClick={dismiss}
              data-testid="pwa-install-dismiss"
              className="rounded-full text-stone-500 hover:text-stone-700 text-sm px-3 py-1.5 transition-colors"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          aria-label="Close"
          data-testid="pwa-install-close"
          className="text-stone-400 hover:text-stone-600 -mt-1 -mr-1"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  if (iosHint) {
    return (
      <div
        data-testid="pwa-ios-hint"
        className="fixed bottom-24 left-1/2 -translate-x-1/2 z-40 w-[92%] max-w-sm bg-white rounded-2xl shadow-2xl border border-stone-200 p-4 animate-fade-up"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-sage-100 flex items-center justify-center shrink-0">
            <Smartphone className="w-5 h-5 text-sage-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-serif text-lg text-stone-900 leading-tight">Use Skinsights like an app</p>
            <p className="text-xs text-stone-500 mt-1 leading-relaxed">
              Tap <Share className="inline w-3.5 h-3.5 align-text-bottom mx-0.5 text-sage-500" />
              <span className="font-medium text-stone-700">Share</span>, then choose
              <span className="font-medium text-stone-700"> "Add to Home Screen"</span>.
            </p>
          </div>
          <button
            onClick={dismiss}
            aria-label="Close"
            data-testid="pwa-ios-hint-close"
            className="text-stone-400 hover:text-stone-600 -mt-1 -mr-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
